import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ProductMatchAutocomplete from '@/components/ProductMatchAutocomplete';
import { ChevronUp, ChevronDown, X, Plus, Pencil } from 'lucide-react';

export const NewOrderDialog = ({
  showNewOrder,
  setShowNewOrder,
  newOrder,
  setNewOrder,
  setVendorSearch,
  showVendorSuggestions,
  setShowVendorSuggestions,
  vendorSuggestions,
  newOrderSubtotal,
  newOrderTotal,
  handleCreateOrder,
  addLineItem,
  removeLineItem,
  moveLineItem,
  updateLineItem,
  updateLineItemFields,
  fetchBenchmark,
  getPriceIndicator,
  priceBenchmarks,
  phases,
  onOpenProductDetail,
  formatCurrency,
}) => (
  <Dialog open={showNewOrder} onOpenChange={setShowNewOrder}>
    <DialogContent className="sm:max-w-2xl border border-zinc-200  rounded-none p-0 max-h-[95vh] overflow-hidden flex flex-col">
      <DialogHeader className="p-4 sm:p-6 pb-0 flex-shrink-0">
        <DialogTitle className="text-2xl font-heading font-bold uppercase tracking-wide">New Order</DialogTitle>
      </DialogHeader>

      <form onSubmit={handleCreateOrder} className="flex flex-col flex-1 overflow-hidden">
        <div className="p-4 sm:p-6 pt-4 space-y-4 overflow-y-auto flex-1">
          {/* Vendor with Autocomplete */}
          <div className="relative">
            <label className="block text-xs font-bold uppercase tracking-wide mb-2">Vendor Name *</label>
            <input
              type="text"
              value={newOrder.vendor_name}
              onChange={(e) => {
                setNewOrder({ ...newOrder, vendor_name: e.target.value });
                setVendorSearch(e.target.value);
                setShowVendorSuggestions(true);
              }}
              onFocus={() => setShowVendorSuggestions(true)}
              onBlur={() => setTimeout(() => setShowVendorSuggestions(false), 200)}
              required
              data-testid="new-order-vendor"
              className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3"
              placeholder="Start typing vendor name..."
            />
            {showVendorSuggestions && vendorSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-zinc-200  max-h-40 overflow-y-auto">
                {vendorSuggestions.map((vendor) => (
                  <button
                    key={`vendor-sugg-${vendor}`}
                    type="button"
                    onClick={() => {
                      setNewOrder({ ...newOrder, vendor_name: vendor });
                      setShowVendorSuggestions(false);
                    }}
                    className="w-full text-left p-3 hover:bg-primary font-medium border-b border-gray-200 last:border-b-0"
                  >
                    {vendor}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">PO Number</label>
              <input
                type="text"
                value={newOrder.po_number}
                onChange={(e) => setNewOrder({ ...newOrder, po_number: e.target.value })}
                data-testid="new-order-po"
                className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3"
                placeholder="PO-2025-001"
              />
            </div>
            {phases.length > 0 && (
              <div>
                <label className="block text-xs font-bold uppercase tracking-wide mb-2">Assign to Phase</label>
                <select
                  value={newOrder.phase_id}
                  onChange={(e) => setNewOrder({ ...newOrder, phase_id: e.target.value })}
                  className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3"
                >
                  <option value="">No Phase</option>
                  {phases.map(phase => (
                    <option key={phase.phase_id} value={phase.phase_id}>{phase.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-2">Description</label>
            <input
              type="text"
              value={newOrder.description}
              onChange={(e) => setNewOrder({ ...newOrder, description: e.target.value })}
              data-testid="new-order-description"
              className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3"
              placeholder="Copper piping and fittings"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">Order Date</label>
              <input
                type="date"
                value={newOrder.order_date}
                onChange={(e) => setNewOrder({ ...newOrder, order_date: e.target.value })}
                className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">Expected Delivery</label>
              <input
                type="date"
                value={newOrder.expected_delivery_date}
                onChange={(e) => setNewOrder({ ...newOrder, expected_delivery_date: e.target.value })}
                className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-2">Status</label>
            <select
              value={newOrder.status}
              onChange={(e) => setNewOrder({ ...newOrder, status: e.target.value })}
              className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3"
            >
              <option value="Draft">Draft</option>
              <option value="Ordered">Ordered</option>
              <option value="Confirmed">Confirmed</option>
              <option value="Shipped">Shipped</option>
              <option value="Delivered">Delivered</option>
              <option value="Issue">Issue</option>
            </select>
          </div>

          {/* Line Items */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="text-sm font-heading font-bold uppercase tracking-wide uppercase tracking-wide">Line Items</label>
            </div>
            <div className="space-y-3">
              {newOrder.line_items.map((item, index) => (
                <div key={item._key || `li-idx-${index}`} className="border border-zinc-200 p-3 bg-zinc-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-zinc-500">Item #{index + 1}</span>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => moveLineItem(index, 'up')}
                        disabled={index === 0}
                        className="p-1 border border-black bg-white disabled:opacity-30"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveLineItem(index, 'down')}
                        disabled={index === newOrder.line_items.length - 1}
                        className="p-1 border border-black bg-white disabled:opacity-30"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeLineItem(index)}
                        disabled={newOrder.line_items.length === 1}
                        className="p-1 border border-black bg-white text-destructive hover:bg-destructive hover:text-white disabled:opacity-30"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <ProductMatchAutocomplete
                    value={item.product_description}
                    onChange={(v) => {
                      updateLineItemFields(index, item.product_id
                        ? { product_description: v, product_id: null, manufacturer_sku: null, match_confidence: null }
                        : { product_description: v });
                      fetchBenchmark(v, index);
                    }}
                    onProductSelect={(sel) => {
                      updateLineItemFields(index, {
                        product_description: sel.product_description,
                        product_id: sel.product_id,
                        manufacturer_sku: sel.manufacturer_sku,
                        match_confidence: sel.match_confidence,
                        ...(sel.unit_of_measure ? { unit: sel.unit_of_measure } : {}),
                      });
                      fetchBenchmark(sel.product_description, index, sel.product_id);
                    }}
                    currentProductId={item.product_id}
                    currentMatchConfidence={item.match_confidence}
                    currentMatchedName={item.product_description}
                    placeholder="Product description"
                    testIdPrefix={`new-line-${index}-product`}
                    inputClassName="w-full h-12 border border-zinc-200 bg-white text-zinc-900 p-3 mb-2"
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                        placeholder="Qty"
                        data-testid={`line-item-qty-${index}`}
                        className="w-full h-12 border border-zinc-200 bg-white text-zinc-900 p-3 text-center"
                      />
                    </div>
                    <div>
                      <select
                        value={item.unit}
                        onChange={(e) => updateLineItem(index, 'unit', e.target.value)}
                        className="w-full h-12 border border-zinc-200 bg-white text-zinc-900 p-2"
                      >
                        <option value="each">each</option>
                        <option value="ft">ft</option>
                        <option value="box">box</option>
                        <option value="spool">spool</option>
                        <option value="roll">roll</option>
                        <option value="bundle">bundle</option>
                        <option value="other">other</option>
                      </select>
                    </div>
                    <div>
                      <input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)}
                        placeholder="$ Price"
                        step="0.01"
                        data-testid={`line-item-price-${index}`}
                        className="w-full h-12 border border-zinc-200 bg-white text-zinc-900 p-3 text-right"
                      />
                    </div>
                  </div>
                  {/* Price Benchmark Indicator */}
                  {priceBenchmarks[index] && (
                    <div className="mt-1" data-testid={`price-benchmark-${index}`}>
                      {(() => {
                        const indicator = getPriceIndicator(item.unit_price, priceBenchmarks[index]);
                        const bm = priceBenchmarks[index];
                        return (
                          <p className={`text-xs ${indicator ? indicator.color : 'text-zinc-500'}`}>
                            {bm.exact_product_match && (
                              <span className="inline-block mr-1 px-1 py-0.5 text-[9px] font-heading font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300">EXACT</span>
                            )}
                            Your avg: <span className="font-bold">{formatCurrency(bm.avg_price)}</span>/{item.unit || 'unit'}
                            {bm.best_vendor && bm.best_price != null && (
                              <> {' '} | Best: <span className="font-bold text-emerald-700">{formatCurrency(bm.best_price)}</span> at {bm.best_vendor}</>
                            )}
                            {!bm.best_vendor && (
                              <> {' '} | Range: {formatCurrency(bm.low_price)}-{formatCurrency(bm.high_price)}</>
                            )}
                            <span className="text-zinc-500"> ({bm.count} orders)</span>
                            {indicator && indicator.label !== 'Good price' && (
                              <span className={`font-bold ml-1 ${indicator.color}`}> — {indicator.label}</span>
                            )}
                            {indicator && indicator.label === 'Good price' && (
                              <span className="font-bold ml-1 text-success"> — Good price</span>
                            )}
                          </p>
                        );
                      })()}
                    </div>
                  )}
                  {/* Catalog product tag (clickable) */}
                  {item.product_id && onOpenProductDetail && (
                    <button
                      type="button"
                      onClick={() => onOpenProductDetail(item.product_id)}
                      data-testid={`line-item-product-tag-${index}`}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] font-heading font-bold uppercase tracking-wider px-1.5 py-0.5 bg-zinc-100 text-zinc-700 border border-zinc-300 hover:bg-primary hover:border-black"
                      title="View product analytics"
                    >
                      <Pencil className="w-2.5 h-2.5" />
                      Catalog product
                    </button>
                  )}
                  <div className="text-right mt-2">
                    <span className="text-sm font-bold">
                      Extended: {formatCurrency((parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0))}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Large Add Line Item Button */}
            <button
              type="button"
              onClick={addLineItem}
              data-testid="add-line-item-btn"
              className="w-full h-10 mt-4 bg-white border-2 border-dashed border-black text-zinc-900 font-bold uppercase flex items-center justify-center gap-2 hover:bg-zinc-50"
            >
              <Plus className="w-5 h-5" />
              Add Line Item
            </button>
          </div>

          {/* Tax and Freight */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">Tax</label>
              <input
                type="number"
                value={newOrder.tax}
                onChange={(e) => setNewOrder({ ...newOrder, tax: e.target.value })}
                step="0.01"
                className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3 text-right"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wide mb-2">Freight/Delivery</label>
              <input
                type="number"
                value={newOrder.freight}
                onChange={(e) => setNewOrder({ ...newOrder, freight: e.target.value })}
                step="0.01"
                className="w-full h-11 border border-zinc-200 bg-white text-zinc-900 text-sm px-3 text-right"
              />
            </div>
          </div>
        </div>

        {/* Running Total - Always Visible */}
        <div className="p-4 sm:p-6 pt-0 border-t-2 border-black bg-white flex-shrink-0">
          <div className="bg-zinc-50 border border-zinc-200 p-4 mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span>Subtotal:</span>
              <span className="font-bold">{formatCurrency(newOrderSubtotal)}</span>
            </div>
            <div className="flex justify-between text-sm mb-1">
              <span>Tax:</span>
              <span className="font-bold">{formatCurrency(parseFloat(newOrder.tax) || 0)}</span>
            </div>
            <div className="flex justify-between text-sm mb-2">
              <span>Freight:</span>
              <span className="font-bold">{formatCurrency(parseFloat(newOrder.freight) || 0)}</span>
            </div>
            <div className="flex justify-between text-xl border-t-2 border-black pt-2">
              <span className="font-heading font-bold uppercase tracking-wide">TOTAL:</span>
              <span className="font-heading font-bold uppercase tracking-wide">{formatCurrency(newOrderTotal)}</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button type="button" onClick={() => setShowNewOrder(false)} className="flex-1 h-10 bg-white text-zinc-900 font-bold uppercase border border-zinc-200">Cancel</button>
            <button type="submit" data-testid="create-order-btn" className="flex-1 h-10 bg-primary text-zinc-900 font-bold uppercase border border-zinc-200  hover:bg-primary-hover active:translate-y-1">Create Order</button>
          </div>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);
